import type { ReactNode } from 'react';
import { Component } from 'react';
import * as Sentry from '@sentry/browser';

import Error from 'app/components/Error';
import { ERROR_TO_FILTER } from 'app/constants/errors';

interface Props {
  children: ReactNode;
  skipRender?: boolean;
  id?: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false
  };

  static displayName = 'ErrorBoundary';

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { id } = this.props;
    if (prevProps.id !== id) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error.message !== ERROR_TO_FILTER) {
      this.setState({ hasError: true });

      Sentry.withScope(scope => {
        scope.setExtras(errorInfo as Record<string, any>);
        scope.setTag('captured_by_widget', 'true');
        Sentry.captureException(error);
      });
    }
  }

  render() {
    const { hasError } = this.state;
    const { children, skipRender } = this.props;

    if (hasError) {
      if (skipRender) {
        return null;
      }

      return <Error />;
    }
    return children;
  }
}

export default ErrorBoundary;
